import { useMutation, useQueryClient } from '@tanstack/react-query';
import { snackbarService } from 'shared/lib/snackbar/service';
import { RQAPICall } from 'store/APIClient';

import type { DataCatalogField } from '../types';

const toggleFieldVisibility = async ({
    table,
    name,
}: {
    table: string;
    name: string;
}) => {
    const response = await RQAPICall({
        endpoint: `graph/table/${table}/field/${name}/visibility`,
        method: 'PUT',
    });

    return response.content.message;
};

/**
 * Mutation for toggling the visibility of a field in the data catalog.
 */
export const useAPIToggleFieldVisibilityMutation = (
    fieldName: string,
): {
    mutate: ({ table, name }: { table: string; name: string }) => void;
} => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: toggleFieldVisibility,

        onSuccess: (message) => {
            queryClient.setQueryData<DataCatalogField[]>(
                ['data_catalog_fields'],
                (prev) =>
                    prev?.map((field) =>
                        field.field === fieldName
                            ? {
                                  ...field,
                                  isVisible: !field.isVisible,
                              }
                            : field,
                    ),
            );
            queryClient.invalidateQueries({
                queryKey: ['get_metrics'],
            });
            queryClient.invalidateQueries({
                queryKey: ['get_dimensions'],
            });
            queryClient.invalidateQueries({
                queryKey: ['get_filters'],
            });
            queryClient.invalidateQueries({
                queryKey: ['available_fields'],
            });
            snackbarService.showSuccess(message);
        },
        meta: { useGlobalOnError: true },
    });
};
